import { Widget } from "@foretagsplatsen/widgetjs";
import Highcharts from "highcharts";
import jQuery from "jquery";
import responsive from "../responsive.js";

/**
 * `highchartWidget` displays a chart and its legend using the
 * Highcharts library.
 */
export default class HighchartWidget extends Widget {
	constructor() {
		super(...arguments);

		this._highchart = undefined;

		this._chartId = "";
		this._chartOptions = {};
		this._legendId = "";

		this._data = [];
	}

	setData(data) {
		this._data = data;
		this.update();
	}

	clearXAxis() {
		if (this._highchart) {
			this._highchart.xAxis.forEach((axis) => {
				axis.remove();
			});
		}
		this._chartOptions.xAxis = [];
	}

	setXAxis(axisList) {
		this.clearXAxis();
		this._chartOptions.xAxis = axisList;
		if (this._highchart) {
			axisList.forEach((axis) => {
				this._highchart.addAxis(axis, true);
			});
		}
	}

	setSeriesSet(options) {
		const seriesSet = options.series;
		const redraw = options.redraw;
		this.clearSeries();

		seriesSet.forEach((seriesOptions) => {
			this.addSeries(seriesOptions, false, true);
		});

		if (this._highchart) {
			if (redraw) {
				this._highchart.redraw();
			}
			this._highchart.hideLoading();
		}
	}

	clearSeries() {
		this._chartOptions.series = [];
		if (this._highchart) {
			this._highchart.series.slice().forEach((series) => {
				series.remove();
			});
		}
	}

	addSeries(seriesOptions, redraw, silent) {
		this._chartOptions.series ||= [];
		this._chartOptions.series.push(seriesOptions);
		if (this._highchart) {
			this._highchart.addSeries(seriesOptions, redraw);
			if (!silent) {
				this._highchart.hideLoading();
			}
		}
	}

	showLoading() {
		if (!this._highchart) {
			return;
		}
		this._highchart.showLoading();
	}

	hideLoading() {
		if (!this._highchart) {
			return;
		}
		this._highchart.hideLoading();
	}

	isEmpty() {
		return this._data.length === 0;
	}

	toggleSeries(seriesId) {
		const series = this._getSeries(seriesId);
		if (series) {
			if (series.visible) {
				series.hide();
			} else {
				series.show();
			}
		}
	}

	setSeriesVisibility(seriesId, visible) {
		const series = this._getSeries(seriesId);
		if (!series) {
			return;
		}

		if (visible) {
			series.show();
		} else {
			series.hide();
		}
	}

	isSeriesVisible(seriesId) {
		const series = this._getSeries(seriesId);
		return series && series.visible;
	}

	update() {
		if (!this._highchart) {
			return;
		}
		this._updateChartData();
	}

	renderContentOn(html) {
		html.div({
			id: this._chartId,
			class: "legend-parent",
			"data-help": this._chartId,
		});

		html.render(this._legend);
		this._setupHighchart();
	}

	/**
	 * Override in subclasses
	 */
	_legend() {}

	_getSeries(seriesId) {
		if (!this._highchart) {
			return null;
		}

		return this._highchart.get(seriesId);
	}

	_updateChartData() {
		if (this.isEmpty()) {
			return;
		}

		if (this._highchart) {
			this._data.forEach((entries, index) => {
				if (!this._highchart.series[index]) {
					return;
				}
				this._highchart.series[index].setData(entries, false);
			});

			this._highchart.hideLoading();
			this._highchart.redraw();
		}

		if (this._legend.update) {
			this._legend.update();
		}
	}

	_setupHighchart() {
		// TODO: We need to know when the widget is added to the
		// DOM (and not to a fragment). This will be done in
		// widgetjs using automatic children registration when
		// rendering a widget. Then we will be able to trigger a
		// hook when a root widget is added to the dom, and
		// recursively call the hook for all children. In the
		// meantime, we need this setTimeout.
		setTimeout(() => {
			this._chartOptions.exporting = {
				enabled: false,
				buttons: {
					printButton: {
						enabled: false,
					},
				},
			};

			this._updateHighchartsDiv();

			if (this._highchart) {
				responsive.onOrientationChange(() => {
					// Try to update the size asap.
					// May fail depending on the browser.
					this._updateSize();

					// Update another time a bit later (when the change is done)
					// to be sure it happens everywhere.
					setTimeout(this._updateSize.bind(this), 500);
				});

				this._highchart.showLoading();
			}
			this._updateChartData(); // set data if we have it
		}, 0);
	}

	_updateHighchartsDiv() {
		const container = document.getElementById(this._chartId);
		if (!container) return;
		this._highchart = Highcharts.chart(container, this._chartOptions);
	}

	_updateSize() {
		const width = jQuery(this._highchart.container).width("100%").width();
		const height = jQuery(this._highchart.container)
			.height("100%")
			.height();
		this._highchart.setSize(width, height, true);
		this._highchart.reflow();
	}
}
